import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  createYowanAdminMemberProfileCompletionRequest,
  createYowanAdminMemberPasswordSetupRequest,
  fetchYowanAdminMemberList,
  resendYowanAdminOnboardingRequest,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const MEMBERS_PAGE_SIZE = 30;
const memberSortOptions = [
  { value: 'updated-desc', label: '更新日が新しい順' },
  { value: 'account-progress', label: 'アカウント進捗順' },
  { value: 'account-progress-desc', label: 'アカウント進捗逆順' },
  { value: 'active-status', label: '会員資格順' },
  { value: 'profile-status', label: 'プロフィール順' },
  { value: 'email-asc', label: 'メールアドレス昇順' },
  { value: 'email-desc', label: 'メールアドレス降順' },
  { value: 'name-asc', label: '氏名昇順' },
  { value: 'name-desc', label: '氏名降順' },
];

const accountColumnStyle = {
  width: '13.5rem',
  minWidth: '13.5rem',
};

const profileColumnStyle = {
  width: '7rem',
  minWidth: '7rem',
};

const dateColumnStyle = {
  width: '11.5rem',
  minWidth: '11.5rem',
};

const actionColumnStyle = {
  width: '19rem',
  minWidth: '19rem',
};

const actionStackStyle = {
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: '0.28rem',
  whiteSpace: 'nowrap',
};

const inlineActionButtonStyle = {
  appearance: 'none',
  border: 0,
  padding: 0,
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  cursor: 'pointer',
  lineHeight: 1.35,
  textAlign: 'left',
  textDecoration: 'underline',
};

const accountCellStyle = {
  display: 'grid',
  justifyItems: 'start',
  gap: '0.35rem',
};

const accountLoginStateStyle = {
  margin: 0,
  color: 'var(--text-muted)',
  fontSize: '0.75rem',
  lineHeight: 1.35,
};

const accountStatusBadgeStyle = {
  whiteSpace: 'nowrap',
};

const memberStatusToUi = (lifecycleStatus) => {
  if (lifecycleStatus === 'active') {
    return { label: '有効', tone: 'published' };
  }

  if (lifecycleStatus === 'withdrawn') {
    return { label: '退会', tone: 'draft' };
  }

  return { label: '停止中', tone: 'draft' };
};

const profileStatusToUi = (isActive) =>
  isActive
    ? { label: '有効', tone: 'published' }
    : { label: '停止', tone: 'draft' };

const getAccountSetupRequest = (row) =>
  row.latestOnboardingRequest?.requestKind === 'member_account_setup'
    ? row.latestOnboardingRequest
    : null;

const getPasswordSetupOperationId = (row) => getAccountSetupRequest(row)?.id || row.id;

const onboardingStatusToUi = (row) => {
  const request = getAccountSetupRequest(row);

  if (request?.statusLabel) {
    const tone = ['send_failed', 'expired', 'canceled'].includes(request.status)
      ? 'draft'
      : 'published';
    return { label: request.statusLabel, tone };
  }

  if (row.accountPasswordSetupRequired) {
    return { label: 'パスワード設定待ち', tone: 'draft' };
  }

  return null;
};

const accountLoginStateLabel = (row) => {
  if (row.accountPasswordSetupRequired) {
    return 'ログイン未設定';
  }

  return row.accountIsActive ? 'ログイン可能' : 'ログイン停止';
};

const accountProgressRank = (row) => {
  const status = getAccountSetupRequest(row)?.status || '';

  if (status) {
    const ranks = {
      send_failed: 0,
      expired: 1,
      draft: 2,
      sent: 3,
      opened: 4,
      submitted: 5,
      canceled: 6,
      completed: 7,
    };

    return ranks[status] ?? 8;
  }

  if (row.accountPasswordSetupRequired) {
    return 3;
  }

  return 8;
};

const sortMemberRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'account-progress' || sortKey === 'account-progress-desc') {
      const leftRank = accountProgressRank(left);
      const rightRank = accountProgressRank(right);

      if (leftRank !== rightRank) {
        return sortKey === 'account-progress'
          ? leftRank - rightRank
          : rightRank - leftRank;
      }

      return left.name.localeCompare(right.name, 'ja');
    }

    if (sortKey === 'active-status') {
      if (left.lifecycleStatus === right.lifecycleStatus) {
        return left.name.localeCompare(right.name, 'ja');
      }
      return left.lifecycleStatus === 'active' ? -1 : 1;
    }

    if (sortKey === 'profile-status') {
      if (left.isActive === right.isActive) {
        return left.name.localeCompare(right.name, 'ja');
      }
      return left.isActive ? -1 : 1;
    }

    if (sortKey === 'email-asc') {
      return left.email.localeCompare(right.email, 'ja');
    }

    if (sortKey === 'email-desc') {
      return right.email.localeCompare(left.email, 'ja');
    }

    if (sortKey === 'name-asc') {
      return left.name.localeCompare(right.name, 'ja');
    }

    if (sortKey === 'name-desc') {
      return right.name.localeCompare(left.name, 'ja');
    }

    const leftDate = Date.parse(left.updatedAt.replace(' ', 'T'));
    const rightDate = Date.parse(right.updatedAt.replace(' ', 'T'));
    const safeLeft = Number.isNaN(leftDate) ? 0 : leftDate;
    const safeRight = Number.isNaN(rightDate) ? 0 : rightDate;
    return safeRight - safeLeft;
  });

  return nextRows;
};

const AdminMembersListPage = () => {
  const content = yowanPageContent['/admin/members'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [lifecycleStatus, setLifecycleStatus] = useState('');
  const [sortKey, setSortKey] = useState('updated-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [resendingRequestId, setResendingRequestId] = useState('');
  const [sendingProfileRequestMemberId, setSendingProfileRequestMemberId] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadMembers = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const payload = await fetchYowanAdminMemberList({
          page: currentPage,
          pageSize: MEMBERS_PAGE_SIZE,
          q: searchQuery.trim(),
          lifecycleStatus,
        });

        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setApiCount(payload.count);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setApiCount(0);
        setLoadError('会員情報を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadMembers();

    return () => {
      isMounted = false;
    };
  }, [content, currentPage, lifecycleStatus, searchQuery]);

  if (!content) {
    return null;
  }

  const sortedRows = sortMemberRows(rows, sortKey);
  const totalPages = Math.max(1, Math.ceil(apiCount / MEMBERS_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * MEMBERS_PAGE_SIZE;
  const visibleRows = sortedRows;
  const buildNewAccessHref = (baseHref, row) => {
    const searchParams = new URLSearchParams(preservedDomainSearch);

    searchParams.set('memberId', row.id);
    searchParams.set('memberLabel', `${row.name || '氏名未設定'} / ${row.email || '-'}`);

    const queryString = searchParams.toString();
    return `${baseHref}${queryString ? `?${queryString}` : ''}`;
  };
  const buildMemberAccessHref = (row) =>
    row.memberAccess?.id
      ? appendYowanPreviewQueryIfNeeded(`/admin/access/member/${row.memberAccess.id}/edit`, preservedDomainSearch)
      : buildNewAccessHref('/admin/access/member/new', row);
  const buildOperatorAccessHref = (row) =>
    row.operatorAccess?.id
      ? appendYowanPreviewQueryIfNeeded(`/admin/access/operator/${row.operatorAccess.id}/edit`, preservedDomainSearch)
      : buildNewAccessHref('/admin/access/operator/new', row);
  const buildEventProfileHref = (baseCreateHref, baseEditHref, profile, row) =>
    profile?.id
      ? appendYowanPreviewQueryIfNeeded(`${baseEditHref}/${profile.id}/edit`, preservedDomainSearch)
      : buildNewAccessHref(baseCreateHref, row);
  const canSendProfileCompletionRequest = (row) =>
    row.accountPasswordSetupRequired || row.accountIsActive !== true;
  const handleSendPasswordSetupRequest = async (row) => {
    const request = getAccountSetupRequest(row);
    const requestId = request?.id;
    const canResendExistingRequest = requestId
      && !['canceled', 'completed'].includes(request.status);
    const operationId = requestId || row.id;

    if (!row.accountPasswordSetupRequired) {
      return;
    }

    if (!window.confirm('パスワード設定通知を送信しますか？')) {
      return;
    }

    setResendingRequestId(operationId);

    try {
      const nextRequest = canResendExistingRequest
        ? await resendYowanAdminOnboardingRequest(requestId)
        : await createYowanAdminMemberPasswordSetupRequest(row.id);

      setRows((currentRows) =>
        currentRows.map((currentRow) =>
          currentRow.id === row.id
            ? {
                ...currentRow,
                latestOnboardingRequest: nextRequest,
                accountPasswordSetupRequired: true,
              }
            : currentRow
        )
      );
      window.alert('パスワード設定通知を送信しました。');
    } catch (error) {
      window.alert('パスワード設定通知を送信できませんでした。状態を確認してください。');
    } finally {
      setResendingRequestId('');
    }
  };
  const handleSendProfileCompletionRequest = async (row) => {
    if (!window.confirm('基本情報入力依頼を送信しますか？')) {
      return;
    }

    setSendingProfileRequestMemberId(row.id);

    try {
      await createYowanAdminMemberProfileCompletionRequest(row.id);
      window.alert('基本情報入力依頼を送信しました。');
    } catch (error) {
      window.alert('基本情報入力依頼を送信できませんでした。状態を確認してください。');
    } finally {
      setSendingProfileRequestMemberId('');
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/members" routeKey="/admin/members">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="primary-action"
            className="c-form__submit"
            to={appendYowanPreviewQueryIfNeeded(content.primaryAction.href, preservedDomainSearch)}
          >
            {content.primaryAction.label}
          </Link>,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${apiCount}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="氏名 / ふりがな / メールアドレス / 電話番号"
        sortOptions={memberSortOptions}
      >
        <div className="yowan-admin-toolbar__group">
          <label className="c-form__field yowan-admin-toolbar__field">
            <span className="c-form__label">会員資格</span>
            <select
              className="c-form__control yowan-admin-toolbar__sort"
              value={lifecycleStatus}
              onChange={(event) => {
                setLifecycleStatus(event.target.value);
                setCurrentPage(1);
              }}
            >
              {content.filters.lifecycleStatus.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </AdminListToolbar>
      <AdminRelatedLinks
        links={content.relatedLinks}
        preservedDomainSearch={preservedDomainSearch}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">氏名 / メールアドレス</th>
              <th scope="col">会員資格</th>
              <th scope="col" style={profileColumnStyle}>プロフィール</th>
              <th scope="col" style={accountColumnStyle}>アカウント</th>
              <th scope="col" style={dateColumnStyle}>作成日</th>
              <th scope="col" style={dateColumnStyle}>更新日</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => {
              const statusUi = memberStatusToUi(row.lifecycleStatus);
              const profileUi = profileStatusToUi(row.isActive);
              const onboardingUi = onboardingStatusToUi(row);

              return (
                <tr key={row.id}>
                  <td>{startIndex + index + 1}</td>
                  <td>
                    <p className="yowan-admin-resource-table__title">{row.name}</p>
                    <p className="yowan-admin-resource-table__subline">{row.email}</p>
                  </td>
                  <td>
                    <span
                      className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusUi.tone}`}
                    >
                      {statusUi.label}
                    </span>
                  </td>
                  <td style={profileColumnStyle}>
                    <span
                      className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${profileUi.tone}`}
                    >
                      {profileUi.label}
                    </span>
                  </td>
                  <td style={accountColumnStyle}>
                    <div style={accountCellStyle}>
                      {onboardingUi ? (
                        <span
                          className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${onboardingUi.tone}`}
                          style={accountStatusBadgeStyle}
                        >
                          {onboardingUi.label}
                        </span>
                      ) : null}
                      <p style={accountLoginStateStyle}>
                        {accountLoginStateLabel(row)}
                      </p>
                    </div>
                  </td>
                  <td style={dateColumnStyle}>{row.createdAt || '-'}</td>
                  <td style={dateColumnStyle}>{row.updatedAt || '-'}</td>
                  <td style={actionColumnStyle}>
                    <div
                      className="yowan-admin-resource-table__actions"
                      style={actionStackStyle}
                    >
                      <Link to={appendYowanPreviewQueryIfNeeded(row.editHref, preservedDomainSearch)}>編集</Link>
                      <Link to={buildMemberAccessHref(row)}>会員資格制御</Link>
                      <Link to={buildOperatorAccessHref(row)}>管理権限制御</Link>
                      <Link
                        to={buildEventProfileHref(
                          '/admin/events/instructors/new',
                          '/admin/events/instructors',
                          row.instructorProfile,
                          row,
                        )}
                      >
                        {row.instructorProfile?.id ? '講師プロフィール編集' : '講師プロフィール作成'}
                      </Link>
                      <Link
                        to={buildEventProfileHref(
                          '/admin/events/organizers/new',
                          '/admin/events/organizers',
                          row.organizerProfile,
                          row,
                        )}
                      >
                        {row.organizerProfile?.id ? '主催者プロフィール編集' : '主催者プロフィール作成'}
                      </Link>
                      {canSendProfileCompletionRequest(row) ? (
                        <button
                          type="button"
                          aria-label={`${row.name}へ基本情報入力依頼を送信`}
                          style={{
                            ...inlineActionButtonStyle,
                            cursor: sendingProfileRequestMemberId === row.id ? 'wait' : 'pointer',
                            opacity: sendingProfileRequestMemberId === row.id ? 0.56 : 1,
                          }}
                          disabled={sendingProfileRequestMemberId === row.id}
                          onClick={() => handleSendProfileCompletionRequest(row)}
                        >
                          {sendingProfileRequestMemberId === row.id
                            ? '送信中...'
                            : '基本情報入力依頼を送信'}
                        </button>
                      ) : null}
                      {row.accountPasswordSetupRequired ? (
                        <button
                          type="button"
                          aria-label={`${row.name}へパスワード設定通知を送信`}
                          style={{
                            ...inlineActionButtonStyle,
                            cursor: resendingRequestId === getPasswordSetupOperationId(row) ? 'wait' : 'pointer',
                            opacity: resendingRequestId === getPasswordSetupOperationId(row) ? 0.56 : 1,
                          }}
                          disabled={resendingRequestId === getPasswordSetupOperationId(row)}
                          onClick={() => handleSendPasswordSetupRequest(row)}
                        >
                          {resendingRequestId === getPasswordSetupOperationId(row)
                            ? '送信中...'
                            : 'パスワード設定通知を送信'}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && visibleRows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  {loadError || '表示できる会員がありません。'}
                </td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={8}>
                  <LoadingSpinner size="sm" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminMembersListPage;
